import React, { useCallback } from "react";
import * as THREE from "three";
import { CircleButton } from "../../../shared/ui";
import EditPencil from "../../../icons/EditPencil";
import shallow from "zustand/shallow";
import styles from "./SectionLabel.module.scss";
import Remove from "../../../icons/Remove/Remove";
import AnimationIcon from "../../../icons/Animation/Animation";
import { useCurrent } from "../../../../hooks/useCurrent";
import { SectionInterface } from "../../../../store/types/store";
import { useShelvingStore, useStoreSystem } from "../../../../store";

type LabelValue = number | string | React.ReactElement | boolean;
type LabelType = "info" | "button" | "edit" | "indent" | "item" | "addParts";

interface SectionLabelProps {
    left: number;
    top: number;
    value: LabelValue;
    type?: LabelType;
    isChecked: boolean;
    id: string;
    onClick?(): void;
    section: SectionInterface | undefined;
}

interface SectionLabelComponentProps {
    pos: number[];
    camera: any;
    canvasSize: { width: number; height: number };
    value: LabelValue;
    type?: LabelType;
    isChecked?: boolean;
    id: string;
    onClick?(): void;
    //FIXME пока section с знаком ? надо смотреть зависимости интерфейса
    section?: SectionInterface;
}

const SectionLabel: React.FunctionComponent<SectionLabelProps> = ({
    left = 0,
    top = 0,
    value = 0,
    isChecked = false,
    type = "info",
    id,
    onClick,
    section,
}) => {
    const { getCurrentShelving, updateBrackets } = useShelvingStore(
        (state) => ({
            getCurrentShelving: state.getCurrentShelving,
            updateBrackets: state.updateBrackets,
        }),
        shallow
    );

    const { animationAction, setEditSection } = useStoreSystem(
        (state) => ({
            animationAction: state.animationAction,
            setEditSection: state.setEditSection,
        }),
        shallow
    );

    const sections = getCurrentShelving().sections;
    const { deleteCurrentEditItem, currentSectionId, currentEditItem, currentSection } = useCurrent();
    const { setSectionEditModal, setItemEditModal } = useStoreSystem((state) => ({
        setSectionEditModal: state.setSectionEditModal,
        setItemEditModal: state.setItemEditModal,
    }));
    const deleteItem = useCallback(() => {
        /** Т.к. после deleteCurrentEditItem itemId and sectionId обнуляются */
        const deletedItem = JSON.parse(JSON.stringify(currentEditItem));
        deleteCurrentEditItem();
        /** position[1] нужен для того, чтобы обновлять кронштейны в секции
         * на высоте, на которой находилась полка, т.к. item там уже будет отсутствовать после удаления
         */
        if (currentSectionId && deletedItem?.position && deletedItem?.position[1]) {
            updateBrackets("", currentSectionId, deletedItem.position[1]);
        }
    }, [updateBrackets, deleteCurrentEditItem, sections]);

    const currentItem = sections
        .map((section) => section.items.find((item) => item.id === currentEditItem.id))
        .find((item) => item !== undefined);

    if (type === "info") {
        return (
            <div
                id={"label1"}
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    backgroundColor: "#ffffff",
                    color: "#838280",
                    borderRadius: "15px",
                    width: "50px",
                    height: "30px",
                    lineHeight: "30px",
                    textAlign: "center",
                    transform: `translate(${left - 25}px, ${top}px)`,
                    userSelect: "none",
                    boxShadow: "0px 4px 4px rgba(17, 17, 17, 0.36)",
                    cursor: "pointer",
                }}
            >
                {value}
            </div>
        );
    }
    if (type === "button") {
        return (
            <CircleButton
                onClick={
                    onClick
                        ? onClick
                        : () => {
                              setEditSection(id);
                              const currentSection = sections.find((section) => section.id === id);
                              setSectionEditModal({
                                  isOpen: true,
                                  section: currentSection,
                                  type: "fill",
                              });
                          }
                }
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    height: "30px",
                    width: "30px",
                    backgroundColor: "#ffffff",
                    transform: `translate(${left - 15}px, ${top}px)`,
                    cursor: "pointer",
                }}
            >
                {value}
            </CircleButton>
        );
    }
    if (type === "edit" || type === "indent") {
        return (
            <EditPencil
                onClick={
                    onClick
                        ? onClick
                        : () => {
                              setSectionEditModal({ isOpen: true, section: section, type });
                          }
                }
                isChecked={isChecked}
                style={{
                    transform: `translate(${left - 15}px, ${top}px)`,
                }}
            />
        );
    }
    if (type === "item") {
        return (
            <div
                id={"edit-section-label"}
                className={styles.editSectionLabel}
                style={{
                    transform: `translate(${left - 44}px, ${top}px)`,
                }}
            >
                <Remove onClick={onClick ? onClick : deleteItem} variant={"primary"} />
                {animationAction && <AnimationIcon onClick={animationAction} customVariant={"primary"} />}
            </div>
        );
    }
    if (type === "addParts") {
        return currentItem?.rods?.length === 0 &&
            currentItem.amps?.left?.elements?.length === 0 &&
            currentItem.amps?.right?.elements?.length === 0 &&
            currentItem?.edges?.length === 0 ? null : (
            <CircleButton
                onClick={
                    onClick
                        ? onClick
                        : () => {
                              const currentItem = sections
                                  .map((section) => section.items.find((item) => item.id === currentEditItem.id))
                                  .find((item) => item !== undefined);

                              const currentSection = sections.find((section) =>
                                  section.items.find((item) => item.id === currentEditItem.id)
                              );

                              setItemEditModal({
                                  isOpen: true,
                                  item: currentItem,
                                  section: currentSection,
                              });
                          }
                }
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    height: "30px",
                    width: "30px",
                    backgroundColor: "#ffffff",
                    transform: `translate(${left + 14}px, ${top}px)`,
                    cursor: "pointer",
                }}
            >
                {value}
            </CircleButton>
        );
    }

    if (type === "info") {
        return (
            <div
                id={"label1"}
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    backgroundColor: "#ffffff",
                    color: "#838280",
                    borderRadius: "15px",
                    width: "50px",
                    height: "30px",
                    lineHeight: "30px",
                    textAlign: "center",
                    transform: `translate(${left - 25}px, ${top}px)`,
                    userSelect: "none",
                    boxShadow: "0px 4px 4px rgba(17, 17, 17, 0.36)",
                    cursor: "pointer",
                }}
            >
                {value}
            </div>
        );
    }

    return null;
};

export const SectionLabelComponent: React.FunctionComponent<SectionLabelComponentProps> = ({
    pos,
    camera,
    canvasSize,
    value,
    type,
    id,
    onClick,
    section,
}) => {
    if (pos.length === 0) return null;
    const position = new THREE.Vector3(...pos);

    position.project(camera);

    const left = ((position.x + 1) * canvasSize?.width) / 2;
    const top = (-(position.y - 1) * canvasSize?.height) / 2;

    return (
        <SectionLabel
            id={id}
            isChecked={true}
            top={top}
            left={left}
            value={value}
            type={type}
            onClick={onClick}
            section={section}
        />
    );
};
